import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { useTagDetail } from "@/hooks/useTagDetail";
import { TagDevicesTab } from "@/components/tags/detail/TagDevicesTab";
import { TagProfilesTab } from "@/components/tags/detail/TagProfilesTab";
import { TagApplicationsTab } from "@/components/tags/detail/TagApplicationsTab";
import { TagConfigurationsTab } from "@/components/tags/detail/TagConfigurationsTab";
import { TagPatchPoliciesTab } from "@/components/tags/detail/TagPatchPoliciesTab";
import { TagUsersTab } from "@/components/tags/detail/TagUsersTab";
import { TagIcon } from "@/components/tags/TagIcon";
import { EditTagDialog } from "@/components/tags/EditTagDialog";
import { useToast } from "@/hooks/use-toast";
import * as tagsApi from "@/services/tagsApi";

export default function TagDetailPage() {
  const { tagId } = useParams<{ tagId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("devices");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { tag, resourceCounts, isLoading, error, refetch } = useTagDetail(tagId!);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await tagsApi.deleteTagById(tagId!);
      toast({ title: "Tag deleted", description: "Tag has been deleted successfully." });
      navigate("/tags");
    } catch (error) {
      console.error("Error deleting tag:", error);
      toast({ title: "Error", description: "Failed to delete tag. Please try again.", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleTagUpdated = () => { refetch(); };

  if (error || !tag) {
    return (
      <div className="min-h-screen bg-background p-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">Tag Not Found</h1>
          <p className="text-muted-foreground mb-6">The tag you're looking for doesn't exist or you don't have permission to view it.</p>
          <Button asChild><Link to="/tags"><ArrowLeft className="h-4 w-4 mr-2" />Back to Tags</Link></Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="border-b bg-card/30">
        <div className="flex items-center justify-between px-6 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/tags")}
            className="relative overflow-hidden group transition-all duration-300
              before:content-[''] before:absolute before:inset-0 before:rounded-md
              before:bg-gradient-radial before:from-white/30 before:via-white/20 before:to-white/10
              before:dark:from-white/20 before:dark:via-white/10 before:dark:to-transparent
              before:opacity-0 before:backdrop-blur-xl before:transition-all before:duration-300
              before:scale-95 before:translate-y-1
              hover:before:opacity-100 hover:before:scale-100 hover:before:translate-y-0
              hover:shadow-lg hover:shadow-white/10 dark:hover:shadow-black/20
              after:content-[''] after:absolute after:inset-0 after:rounded-md
              after:border after:border-white/30 after:dark:border-white/20
              after:opacity-0 after:transition-opacity after:duration-300
              hover:after:opacity-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2 relative z-10" />
            <span className="relative z-10">Back to Tags</span>
          </Button>
          <ActionsDropdown align="end" actions={[
            { label: "Edit Tag", icon: Edit, onClick: () => setShowEditDialog(true) },
            { label: "Delete Tag", icon: Trash2, onClick: () => setShowDeleteDialog(true), destructive: true },
          ]} />
        </div>
        <div className="px-6 pb-8 pt-4">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: tag.color + '15', border: `1px solid ${tag.color}40` }}>
              <TagIcon iconName={tag.icon} color={tag.color} size={24} />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-foreground">{tag.name}</h1>
              {tag.description && <p className="text-muted-foreground mt-1">{tag.description}</p>}
              <div className="flex items-center space-x-3 mt-3">
                <Badge variant={tag.type === 'dynamic' ? 'default' : 'secondary'} className="text-xs">
                  {tag.type === 'dynamic' ? 'Dynamic' : 'Static'}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Created {new Date(tag.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-semibold text-foreground">
              {resourceCounts.devices + resourceCounts.profiles + resourceCounts.applications + 
               resourceCounts.configurations + resourceCounts.patchPolicies + resourceCounts.users}
            </div>
            <div className="text-xs text-muted-foreground">Total Resources</div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="px-6 py-4 bg-card/30">
            <TabsList>
              <TabsTrigger value="devices"><span className="font-medium">Devices</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.devices}</span></TabsTrigger>
              <TabsTrigger value="profiles"><span className="font-medium">Profiles</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.profiles}</span></TabsTrigger>
              <TabsTrigger value="applications"><span className="font-medium">Apps & Settings</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.applications}</span></TabsTrigger>
              <TabsTrigger value="configurations"><span className="font-medium">Configurations</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.configurations}</span></TabsTrigger>
              <TabsTrigger value="policies"><span className="font-medium">Patch Policies</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.patchPolicies}</span></TabsTrigger>
              <TabsTrigger value="users"><span className="font-medium">Users</span><span className="ml-2 text-xs bg-muted px-1.5 py-0.5 rounded-full">{resourceCounts.users}</span></TabsTrigger>
            </TabsList>
          </div>
          <div className="p-6">
            <TabsContent value="devices" className="mt-0"><TagDevicesTab tagId={tagId!} tagName={tag?.name} /></TabsContent>
            <TabsContent value="profiles" className="mt-0"><TagProfilesTab tagId={tagId!} /></TabsContent>
            <TabsContent value="applications" className="mt-0"><TagApplicationsTab tagId={tagId!} /></TabsContent>
            <TabsContent value="configurations" className="mt-0"><TagConfigurationsTab tagId={tagId!} /></TabsContent>
            <TabsContent value="policies" className="mt-0"><TagPatchPoliciesTab tagId={tagId!} /></TabsContent>
            <TabsContent value="users" className="mt-0"><TagUsersTab tagId={tagId!} /></TabsContent>
          </div>
        </Tabs>
      </div>

      {tag && <EditTagDialog open={showEditDialog} onOpenChange={setShowEditDialog} tag={tag} onTagUpdated={handleTagUpdated} />}

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{tag.name}"? This action cannot be undone.
              <br /><br />
              <strong>This will affect:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>{resourceCounts.devices} devices</li>
                <li>{resourceCounts.profiles} profiles</li>
                <li>{resourceCounts.applications} applications</li>
                <li>{resourceCounts.configurations} configurations</li>
              </ul>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isDeleting ? "Deleting..." : "Delete Tag"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
